import React, { useState } from 'react';
import { FaMobileAlt, FaStore, FaRocket, FaCheckCircle, FaChartLine, FaUsers } from 'react-icons/fa';
import '../../styles/Ussd.scss';
import ussdSolution from '../../assets/optimized/ussd-solution.webp';

const ldJsonUssd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      "@id": "https://taifamobile.co.ke/ussd/#service",
      "name": "USSD Services",
      "url": "https://taifamobile.co.ke/ussd",
      "serviceType": "USSD Application & Hosting",
      "provider": { "@id": "https://taifamobile.co.ke/#organization" },
      "description": "Secure USSD applications for payments, self-service, onboarding, and data collection across Kenya."
    },
    {
      "@type": "BreadcrumbList",
      "@id": "https://taifamobile.co.ke/ussd/#breadcrumb",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://taifamobile.co.ke" },
        { "@type": "ListItem", "position": 2, "name": "USSD Services", "item": "https://taifamobile.co.ke/ussd" }
      ]
    },
    {
      "@type": "FAQPage",
      "@id": "https://taifamobile.co.ke/ussd/#faq",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is the difference between Shared and Dedicated USSD codes?",
          "acceptedAnswer": { "@type": "Answer", "text": "Shared USSD codes are cost-effective with quick setup (e.g., *456*01#) and shared infrastructure. Dedicated codes give you a unique short code (e.g., *123#) with full branding and control." }
        },
        {
          "@type": "Question",
          "name": "How long does it take to set up a USSD service?",
          "acceptedAnswer": { "@type": "Answer", "text": "Shared USSD is instant and ready for setup. Dedicated codes depend on the network operator's approval process." }
        },
        {
          "@type": "Question",
          "name": "What are Pre-paid vs Post-paid USSD sessions?",
          "acceptedAnswer": { "@type": "Answer", "text": "Pre-paid sessions are charged directly to the user's airtime. Post-paid sessions are billed to the business with volume-based discounts." }
        },
        {
          "@type": "Question",
          "name": "Does USSD work on all networks in Kenya?",
          "acceptedAnswer": { "@type": "Answer", "text": "Yes, our USSD services work seamlessly on Safaricom and Airtel networks." }
        },
        {
          "@type": "Question",
          "name": "Can I integrate USSD with my existing systems?",
          "acceptedAnswer": { "@type": "Answer", "text": "Yes, we provide a robust RESTful API for easy integration with your CRM, payment systems, or custom applications." }
        },
        {
          "@type": "Question",
          "name": "How do I get started with USSD?",
          "acceptedAnswer": { "@type": "Answer", "text": "Contact our team. We'll guide you through choosing the right plan, setup, and deployment." }
        }
      ]
    }
  ]
};

// eslint-disable-next-line react-refresh/only-export-components
export const meta = () => [
  { title: 'USSD Services Kenya | Dedicated & Shared USSD Codes - Taifa Mobile' },
  {
    name: 'description',
    content: 'Build secure USSD applications for payments, self-service, surveys, and onboarding in Kenya. Shared and dedicated USSD codes on Safaricom and Airtel.'
  }
];

const Ussd = () => {
  const [activeTab, setActiveTab] = useState('dedicated');

  const ussdSolutions = [
    {
      id: 'dedicated',
      icon: <FaMobileAlt className="icon" />,
      title: "Dedicated USSD Codes",
      tagline: "Your Brand, Your Code",
      description: "Get a unique short code (e.g., *123#) with full control over the customer journey.",
      features: ["Fully branded customer experience", "Ideal for financial services", "Supports complex menu structures", "Maximum flexibility and customization"],
      example: "Perfect for banking services, mobile money, and customer support systems",
      color: "#008c95"
    },
    {
      id: 'shared',
      icon: <FaStore className="icon" />,
      title: "Shared USSD Codes",
      tagline: "Affordable, Quick Setup",
      description: "Quick setup with a shared code and your own dedicated menu slot (e.g., *456*01#).",
      features: ["Quick setup within 48 hours", "Perfect for surveys and promotions", "Private experience on shared infrastructure", "Cost-effective solution"],
      example: "Ideal for short-term campaigns, surveys, and small business applications",
      color: "#e97525"
    }
  ];

  const universalBenefits = [
    { title: "100% Phone Coverage", description: "USSD works on every mobile phone in Kenya - no smartphone or internet required.", icon: <FaMobileAlt className="benefit-icon" />, color: "#e97525" },
    { title: "Fast Shared Setup", description: "Shared USSD codes are ready for immediate setup - ideal for pilots and time-sensitive launches.", icon: <FaRocket className="benefit-icon" />, color: "#008c95" },
    { title: "Volume-Based Pricing", description: "Competitive session rates with bundle discounts for growing usage.", icon: <FaChartLine className="benefit-icon" />, color: "#91a2a1" },
    { title: "Dedicated Integration Support", description: "Our technical team assists with API setup, menu design, and testing.", icon: <FaUsers className="benefit-icon" />, color: "#e97525" }
  ];

  const keyFeaturesOverview = [
    { title: "Dedicated & Shared Codes", description: "Get a unique *123# for full brand control, or use a shared code for fast launch.", icon: <FaMobileAlt className="benefit-icon" />, color: "#008c95" },
    { title: "API Integration", description: "Connect USSD sessions to your CRM, payment gateway, or core system using RESTful API.", icon: <FaChartLine className="benefit-icon" />, color: "#e97525" },
    { title: "Real-Time Session Handling", description: "Capture input, validate actions, and return menu responses instantly.", icon: <FaCheckCircle className="benefit-icon" />, color: "#91a2a1" },
    { title: "Payment & Transaction Support", description: "Enable M-PESA and Airtel Money transactions directly through USSD menus.", icon: <FaStore className="benefit-icon" />, color: "#008c95" },
    { title: "Complex Menu Structures", description: "Build multi-level, branching USSD menus for onboarding and self-service.", icon: <FaUsers className="benefit-icon" />, color: "#e97525" },
    { title: "Secure & Compliant", description: "Session-level encryption and compliance with Safaricom and Airtel standards.", icon: <FaCheckCircle className="benefit-icon" />, color: "#91a2a1" }
  ];

  const ussdUseCases = [
    { title: "Fintech & Mobile Money", description: "Balance checks, transaction initiation, loan applications, and payment confirmations.", icon: <FaStore className="benefit-icon" />, color: "#e97525" },
    { title: "SACCOs & Cooperatives", description: "Member self-service, loan status checks, and contribution management.", icon: <FaUsers className="benefit-icon" />, color: "#008c95" },
    { title: "Healthcare", description: "Appointment booking, prescription status, and health information lookup.", icon: <FaCheckCircle className="benefit-icon" />, color: "#91a2a1" },
    { title: "Government & Public Services", description: "Citizen registration, service access, and public feedback collection.", icon: <FaRocket className="benefit-icon" />, color: "#e97525" },
    { title: "Logistics & Retail", description: "Order tracking, delivery confirmation, and customer self-service portals.", icon: <FaChartLine className="benefit-icon" />, color: "#008c95" }
  ];

  const handleGetStarted = () => window.location.href = '/contact';

  return (
    <div className="ussd-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJsonUssd) }} />

      {/* Hero Section */}
      <section className="ussd-hero">
        <div className="container hero-grid">
          <div className="hero-text">
            <h1>USSD Services for Businesses in Kenya</h1>
            <p>Deploy interactive USSD menus for payments, self-service, onboarding, and data collection — accessible on any phone, on any network.</p>
            <p className="hero-links">
              Explore related services: <a href="/payment">Mobile Payments</a> and <a href="/shortcode">Short Codes</a>.
            </p>
            <div className="hero-actions">
              <button className="cta-button primary" onClick={handleGetStarted}>
                <FaRocket /> Build Your USSD Solution
              </button>
              <a className="cta-link" href="#ussd-pricing">View Pricing</a>
            </div>
            <div className="hero-trust">
              <span>Works on All Phones (No Internet Needed)</span>
              <span>Safaricom & Airtel</span>
              <span>API-Ready</span>
            </div>
          </div>
          <div className="hero-image-wrapper">
            <img src={ussdSolution} alt="USSD Solutions" className="hero-image" />
          </div>
        </div>
      </section>

      {/* USSD Solutions Section */}
      <section className="ussd-types-section">
        <div className="container">
          <div className="section-header">
            <h2>Choose Your USSD Strategy</h2>
            <p>Pick the USSD solution that best fits your needs</p>
          </div>
          <div className="tabs">
            {ussdSolutions.map((solution) => (
              <button
                key={solution.id}
                className={`tab ${activeTab === solution.id ? 'active' : ''}`}
                onClick={() => setActiveTab(solution.id)}
                style={{ backgroundColor: activeTab === solution.id ? solution.color : '' }}
              >
                {solution.icon} {solution.title}
              </button>
            ))}
          </div>
          <div className="ussd-type-card-wrapper">
            {ussdSolutions.filter((s) => s.id === activeTab).map((solution) => (
              <div key={solution.id} className="ussd-type-card" style={{ borderColor: solution.color }}>
                <div className="card-header" style={{ borderLeftColor: solution.color }}>
                  <div className="icon-wrapper" style={{ color: solution.color }}>{solution.icon}</div>
                  <div>
                    <h3>{solution.title}</h3>
                    <p className="tagline">{solution.tagline}</p>
                  </div>
                </div>
                <div className="card-body">
                  <p className="description">{solution.description}</p>
                  <div className="features-list">
                    <h4>Key Features:</h4>
                    <ul>
                      {solution.features.map((feature, i) => (
                        <li key={i}><FaCheckCircle style={{ color: solution.color }} /> {feature}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="example" style={{ backgroundColor: `${solution.color}20` }}>
                    <h4>Use Case:</h4>
                    <p>"{solution.example}"</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="benefits-section">
        <div className="container">
          <div className="section-header">
            <h2>Powerful USSD Capabilities Built for Kenyan Businesses</h2>
            <p>From simple shared menus to complex dedicated journeys.</p>
          </div>
          <div className="benefits-grid">
            {keyFeaturesOverview.map((feature, index) => (
              <div className="benefit-card" key={index} style={{ borderColor: feature.color, backgroundColor: `${feature.color}10` }}>
                <div className="icon-container" style={{ color: feature.color }}>{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p className="description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="benefits-section">
        <div className="container">
          <div className="section-header">
            <h2>Why Choose Taifa Mobile for USSD?</h2>
            <p>USSD Kenya coverage with fast setup, volume pricing, and hands-on integration support.</p>
          </div>
          <div className="benefits-grid">
            {universalBenefits.map((benefit, index) => (
              <div className="benefit-card" key={index} style={{ borderColor: benefit.color, backgroundColor: `${benefit.color}10` }}>
                <div className="icon-container" style={{ color: benefit.color }}>{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p className="description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="benefits-section">
        <div className="container">
          <div className="section-header">
            <h2>USSD Applications Across Industries</h2>
            <p>How organizations use USSD Kenya solutions to serve customers and scale operations.</p>
          </div>
          <div className="benefits-grid">
            {ussdUseCases.map((useCase, index) => (
              <div className="benefit-card" key={index} style={{ borderColor: useCase.color, backgroundColor: `${useCase.color}10` }}>
                <div className="icon-container" style={{ color: useCase.color }}>{useCase.icon}</div>
                <h3>{useCase.title}</h3>
                <p className="description">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Updated Pricing Section */}
      <section className="pricing-section" id="ussd-pricing">
        <div className="container">
          <div className="section-header">
            <h2>USSD Pricing - Transparent & Competitive</h2>
            <p>All prices are VAT inclusive unless stated otherwise. Setup is free for shared codes.</p>
          </div>

          {/* Dedicated Codes */}
          <div className="pricing-block">
            <h3 className="pricing-subtitle">Dedicated USSD Codes</h3>
            <div className="pricing-table-wrapper">
              <table className="pricing-table">
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Setup (KES)</th>
                    <th>Monthly (KES)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Dedicated Safaricom</strong></td>
                    <td>290,000</td>
                    <td>58,000</td>
                  </tr>
                  <tr>
                    <td><strong>Dedicated Airtel</strong></td>
                    <td>174,000</td>
                    <td>45,000</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Prepaid Shared USSD */}
          <div className="pricing-block">
            <h3 className="pricing-subtitle">Prepaid Shared USSD (*631# or *252#)</h3>
            <div className="pricing-table-wrapper">
              <table className="pricing-table">
                <thead>
                  <tr>
                    <th>USSD String</th>
                    <th>Setup (KES)</th>
                    <th>Monthly (KES)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>*631*1# - *631*10#</td><td><span className="free">Free</span></td><td>25,000</td></tr>
                  <tr><td>*631*12# - *631*100# (Except Golden Codes)</td><td><span className="free">Free</span></td><td>20,000</td></tr>
                  <tr><td>*631*11#, *631*22#, *631*33#, *631*44#, *631*55#, *631*66#, *631*77#, *631*88#, *631*99#</td><td><span className="free">Free</span></td><td>25,000</td></tr>
                  <tr><td>*631*101# - *631*999# (Except Golden Codes)</td><td><span className="free">Free</span></td><td>10,000</td></tr>
                  <tr><td>*631*111#, *631*222#, *631*333#, *631*444#, *631*555#, *631*666#, *631*777#, *631*888#, *631*999#</td><td><span className="free">Free</span></td><td>15,000</td></tr>
                </tbody>
              </table>
            </div>
            <p className="pricing-note">One Off Monthly Deposit – Ksh 10,000, Ksh 20,000 or Ksh 25,000 + VAT depending on string selected.<br />No Session Cost is chargeable on Code 631.</p>
          </div>

          {/* Postpaid Shared USSD */}
          <div className="pricing-block">
            <h3 className="pricing-subtitle">Postpaid Shared USSD (*631# or *252#)</h3>
            <div className="pricing-table-wrapper">
              <table className="pricing-table">
                <thead>
                  <tr>
                    <th>USSD String</th>
                    <th>Setup (KES)</th>
                    <th>Monthly (KES)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>*631*1# - *631*10#</td><td><span className="free">Free</span></td><td>25,000</td></tr>
                  <tr><td>*631*12# - *631*100# (Except Golden Codes)</td><td><span className="free">Free</span></td><td>20,000</td></tr>
                  <tr><td>*631*11#, *631*22#, *631*33#, *631*44#, *631*55#, *631*66#, *631*77#, *631*88#, *631*99#</td><td><span className="free">Free</span></td><td>25,000</td></tr>
                  <tr><td>*631*101# - *631*999# (Except Golden Codes)</td><td><span className="free">Free</span></td><td>10,000</td></tr>
                  <tr><td>*631*111#, *631*222#, *631*333#, *631*444#, *631*555#, *631*666#, *631*777#, *631*888#, *631*999#</td><td><span className="free">Free</span></td><td>15,000</td></tr>
                </tbody>
              </table>
            </div>
            <p className="pricing-note">One Off Monthly Deposit – Ksh 10,000, Ksh 20,000 or Ksh 25,000 + VAT depending on string selected.</p>
          </div>

          {/* Post-paid Session Costs */}
          <div className="pricing-block">
            <h3 className="pricing-subtitle">Costs per USSD Session (Post-paid)</h3>
            <div className="pricing-table-wrapper">
              <table className="pricing-table">
                <thead>
                  <tr>
                    <th>Bundle</th>
                    <th>Range (Sessions)</th>
                    <th>Price (KES)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Taifa Bundle</td><td>0 - 99,999</td><td>1.5</td></tr>
                  <tr><td>Pro Bundle</td><td>100,000 - 499,999</td><td>1.4</td></tr>
                  <tr><td>Pro+ Bundle</td><td>500,000 - 999,999</td><td>1.3</td></tr>
                  <tr className="enterprise"><td>Enterprise Bundle</td><td>1,000,000 - 4,000,000</td><td>1.2</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <p className="pricing-note">
            All prices are VAT inclusive unless otherwise stated. Contact sales for custom requirements or higher volumes.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="benefits-section">
        <div className="container">
          <div className="section-header">
            <h2>Start Building Your USSD Solution Today</h2>
            <p>Reach every Kenyan customer - on any phone, on any network, without an internet connection.</p>
          </div>
          <div className="cta-actions">
            <button className="cta-button primary" onClick={handleGetStarted}>
              <FaRocket /> Build Your USSD Solution
            </button>
            <a className="cta-link" href="/contact">Talk to Sales</a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
            <p>Common questions about our USSD services answered.</p>
          </div>
          <div className="faq-list">
            <details className="faq-item">
              <summary>What is the difference between Shared and Dedicated USSD codes?</summary>
              <div className="faq-answer"><p>Shared USSD codes are cost-effective with quick setup (e.g., *456*01#) and shared infrastructure. Dedicated codes give you a unique short code (e.g., *123#) with full branding and control.</p></div>
            </details>
            <details className="faq-item">
              <summary>How long does it take to set up a USSD service?</summary>
              <div className="faq-answer"><p>Shared USSD is instant and ready for setup. Dedicated codes depend on the network operator’s approval process.</p></div>
            </details>
            <details className="faq-item">
              <summary>What are Pre-paid vs Post-paid USSD sessions?</summary>
              <div className="faq-answer"><p>Pre-paid sessions are charged directly to the user's airtime. Post-paid sessions are billed to the business with volume-based discounts.</p></div>
            </details>
            <details className="faq-item">
              <summary>Does USSD work on all networks in Kenya?</summary>
              <div className="faq-answer"><p>Yes, our USSD services work seamlessly on Safaricom and Airtel networks.</p></div>
            </details>
            <details className="faq-item">
              <summary>Can I integrate USSD with my existing systems?</summary>
              <div className="faq-answer"><p>Yes, we provide a robust RESTful API for easy integration.</p></div>
            </details>
            <details className="faq-item">
              <summary>How do I get started with USSD?</summary>
              <div className="faq-answer"><p>Contact our team. We’ll guide you through choosing the right plan and deployment.</p></div>
            </details>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Ussd;
